<template>
  <div>
    <!-- Agent, Duration, Payment term, Currency, Delete tab -->
    <ulu-properties
      :setAgent="setAgent"
      :setDuration="setDuration"
      :setPaymentTerm="setPaymentTerm"
      :setPaymentMethod="setPaymentMethod"
      :setCurrency="changeCurrency"
      :deleteTab="deleteThisTab"
    />
    <!-- Company -->
    <b-row style="margin-top: 1em">
      <b-col cols="12" sm="6" md="4" lg="4" xl="4">
        <h5 class="text-info">{{ $t("globals.select_company") }}:</h5>
        <ulu-multiselect
          v-model="selected_company"
          :options="companies"
          label="name"
          track-by="name"
          :placeholder="$t('globals.select_company')"
          @input="changeCompany"
        />
      </b-col>
    </b-row>
    <!-- Items table -->
    <b-row style="margin-top: 2em">
      <b-col>
        <b-table
          small
          bordered
          hover
          :items="tab.data.items"
          :fields="fields"
          @row-clicked="itemClicked"
        >
          <template v-slot:cell(price_one_time)="row">
            {{
              row.item.price_one_time === 0
                ? ""
                : currencyFormat(row.item.price_one_time, getCurrency)
            }}
          </template>
          <template v-slot:cell(price_per_month)="row">
            {{
              row.item.price_per_month === 0
                ? ""
                : currencyFormat(row.item.price_per_month, getCurrency)
            }}
          </template>
          <template v-slot:cell(discount)="row">
            {{ currencyFormat(row.item.discount, getCurrency) }}
          </template>
          <template v-slot:cell(vat)="row">
            {{ row.item.vat.toString() + "%" }}
          </template>
          <template v-slot:cell(vat_price)="row">
            {{
              currencyFormat(
                rowItemTotalVatOnly(
                  row.item.price_one_time,
                  row.item.price_per_month,
                  row.item.quantity,
                  tab.data.contract_duration,
                  row.item.vat,
                  row.item.discount
                ),
                getCurrency
              )
            }}
          </template>
          <template v-slot:cell(total_no_vat)="row">
            {{
              currencyFormat(
                rowItemTotalWithoutVat(
                  row.item.price_one_time,
                  row.item.price_per_month,
                  row.item.quantity,
                  row.item.discount,
                  tab.data.contract_duration
                ),
                getCurrency
              )
            }}
          </template>
          <template v-slot:cell(total)="row">
            {{
              currencyFormat(
                rowItemTotal(
                  row.item.price_one_time,
                  row.item.price_per_month,
                  row.item.quantity,
                  row.item.vat,
                  row.item.discount,
                  tab.data.contract_duration
                ),
                getCurrency
              )
            }}
          </template>
          <!-- row details -->
          <template v-slot:row-details="row">
            <b-card>
              <b-row>
                <b-col cols="3">
                  <span>{{ $t("globals.item") }}:</span>
                  <b-input size="sm" disabled v-model="row.item.item_name">
                  </b-input>
                </b-col>
                <b-col cols="2">
                  <span>{{ $t("globals.price_once") }}:</span>
                  <b-input-group size="sm">
                    <b-input
                      v-model.number="row.item.price_one_time"
                      type="number"
                      size="sm"
                      :disabled="!getUserBrandPermissions.inquiry_edit_price"
                    ></b-input>
                    <b-input
                      :placeholder="
                        currencyFormat(
                          row.item.price_one_time,
                          row.item.currency
                        ).toString()
                      "
                      disabled
                    ></b-input>
                  </b-input-group>
                </b-col>
                <b-col cols="2">
                  <span>{{ $t("globals.price_monthly") }}:</span>
                  <b-input-group size="sm">
                    <b-input
                      v-model.number="row.item.price_per_month"
                      type="number"
                      size="sm"
                      :disabled="!getUserBrandPermissions.inquiry_edit_price"
                    >
                    </b-input>
                    <b-input
                      :placeholder="
                        currencyFormat(
                          row.item.price_per_month,
                          row.item.currency
                        ).toString()
                      "
                      disabled
                    >
                    </b-input>
                  </b-input-group>
                </b-col>
                <b-col>
                  <span>{{ $t("globals.discount_per_item") }}:</span>
                  <b-input-group size="sm">
                    <b-input
                      v-model.number="row.item.discount"
                      min="0"
                      max="100"
                      type="number"
                      size="sm"
                    >
                    </b-input>
                    <b-input
                      :placeholder="
                        currencyFormat(row.item.discount, row.item.currency)
                      "
                      disabled
                    ></b-input>
                  </b-input-group>
                </b-col>
                <b-col cols="1">
                  <span>{{ $t("globals.quantity_short") }}:</span>
                  <b-input
                    v-model.number="row.item.quantity"
                    :disabled="row.item.item_type === ''"
                    min="0"
                    type="number"
                    size="sm"
                  >
                  </b-input>
                </b-col>
                <b-col>
                  <span>{{ $t("globals.vat") }}:</span>
                  <b-input-group append="%" size="sm" style="max-width: 100px">
                    <b-input
                      v-model.number="row.item.vat"
                      min="0"
                      type="number"
                      size="sm"
                    >
                    </b-input>
                  </b-input-group>
                </b-col>
                <b-col cols="1">
                  <b-btn
                    size="sm"
                    variant="danger"
                    style="float: right; margin: 0.2em;"
                    @click="deleteItem(row.item.id)"
                  >
                    <icon name="trash-alt"></icon>
                  </b-btn>
                  <b-btn
                    size="sm"
                    variant="success"
                    style="float: right; margin: 0.2em;"
                    @click.stop="saveChange(row.item)"
                  >
                    <icon name="save"></icon>
                  </b-btn>
                </b-col>
              </b-row>
            </b-card>
          </template>
        </b-table>
      </b-col>
    </b-row>
    <!-- Add item -->
    <b-row>
      <b-col>
        <b-btn @click="addItem" variant="info">
          <icon name="plus"></icon>{{ $t("globals.add_item") }}
        </b-btn>
      </b-col>
    </b-row>
    <hr />
    <!-- Total line -->
    <ulu-grand-total
      :currency="getCurrency"
      :showPayments="showPayments"
      :subTotal="subTotal"
      :vat="vat"
      :grandTotal="grandTotal"
      :payments="payments"
      :oneTime="oneTime"
      :onePayment="onePayment"
    />
    <!-- Notes -->
    <b-row>
      <b-col>
        <span>{{ $t("globals.notes") }}:</span>
        <b-form-textarea
          v-model="tab.data.notes"
          :placeholder="$t('globals.enter_notes')"
          rows="3"
        >
        </b-form-textarea>
      </b-col>
    </b-row>
    <!-- New item modal -->
    <ulu-item-modal :saveItem="saveItem" :listOfPrices="priceList" />
  </div>
</template>

<script>
  import InquiryGroupItem from "@/models/inquiry/inquiry_group_item";
  import { mapGetters, mapMutations, mapActions } from "vuex";
  import {
    currencyFormat,
    priceWithVat,
    rowItemTotal,
    rowItemTotalWithoutVat,
    rowItemTotalVatOnly,
    rowItemTotalInitialPayment
  } from "@/helpers/price";

  import "vue-awesome/icons/trash-alt";
  import "vue-awesome/icons/save";
  import "vue-awesome/icons/plus";

  export default {
    components: {
      Icon: () => import("vue-awesome/components/Icon.vue"),
      "ulu-properties": () =>
        import("@/components/inquiries/new_inquiry/inquiries/Properties"),
      "ulu-grand-total": () =>
        import("@/components/inquiries/new_inquiry/inquiries/GrandTotal"),
      "ulu-item-modal": () =>
        import("@/components/inquiries/new_inquiry/inquiries/ItemModal"),
      "ulu-multiselect": () => import("vue-multiselect")
    },
    props: {
      tab: Object,
      deleteTab: Function
    },
    data() {
      return {
        priceList: [],
        fields: [
          {
            key: "item_name",
            label: this.$t("globals.item")
          },
          {
            key: "price_one_time",
            label: this.$t("globals.price_once")
          },
          {
            key: "price_per_month",
            label: this.$t("globals.price_monthly")
          },
          {
            key: "quantity",
            label: this.$t("globals.quantity")
          },
          {
            key: "discount",
            label: this.$t("globals.discount_per_item"),
            class: "text-right"
          },
          {
            key: "vat",
            label: this.$t("globals.vat")
          },
          {
            key: "total_no_vat",
            label: this.$t("globals.value_no_vat"),
            class: "text-right"
          },
          {
            key: "vat_price",
            label: this.$t("globals.vat_price"),
            class: "text-right"
          },
          {
            key: "total",
            label: this.$t("globals.total"),
            class: "text-right"
          }
        ],
        companies: [],
        selected_company: null
      };
    },
    methods: {
      ...mapActions("axios_mod", ["simpleMutation", "simpleFetch", "simpleWebAppMutation", "simpleWebAppFetch"]),
      
      ...mapMutations([
        "setShowItemModal",
        "setCurrency",
        "setAgent",
        "setCompany",
        "setShowLoadingModal"
      ]),
      currencyFormat,
      priceWithVat,
      rowItemTotal,
      rowItemTotalWithoutVat,
      rowItemTotalVatOnly,
      rowItemTotalInitialPayment,
      setPaymentTerm(value) {
        this.tab.data.payment_term = value;
      },
      setPaymentMethod(value) {
        this.tab.data.payment_method = value;
      },
      setDuration(value) {
        this.tab.data.contract_duration = value;
        this.fetchPriceList();
        this.cleanItems();
      },
      changeCurrency(value) {
        this.setCurrency(value);
        this.fetchPriceList();
        this.cleanItems();
      },
      cleanItems() {
        this.tab.data.items = [];
      },
      addItem() {
        this.setShowItemModal(true);
      },
      saveItem(item) {
        let newItem = new InquiryGroupItem();
        newItem.id = item.id;
        newItem.item_name = item.name;
        newItem.item_type = item.type;
        newItem.price_one_time = item.priceOneTime;
        newItem.price_per_month = item.priceMonthly;
        newItem.discount = item.discount;
        newItem.quantity = item.quantity;
        newItem.vat = item.vat;
        newItem.is_device = item.is_device;
        newItem._showDetails = false;
        newItem._rowVariant = null;
        newItem.exact_code_id = item.exact_code_id;
        newItem.billing_item_id = item.billing_item_id;
        newItem.kpi_type = item.kpi_type;

        this.tab.data.items.push(newItem);

        this.setShowItemModal(false);
      },
      itemClicked(item) {
        item._showDetails = true;
        item._rowVariant = "warning";
        this.tab.data.items.forEach(groupItem => {
          if (item.id == groupItem.id) return;
          groupItem._showDetails = false;
          groupItem._rowVariant = null;
        });
      },
      saveChange(item) {
        item._showDetails = false;
        item._rowVariant = null;
      },
      deleteItem(itemId) {
        this.tab.data.items = this.tab.data.items.filter(
          item => item.id !== itemId
        );
      },
      fetchPriceList() {
        const data = {
          query: `
          query {
            priceList (
              brand: \"${this.getUserSystem.toLowerCase()}\",
              currency: \"${this.getCurrency}\",
              contract_duration: ${this.tab.data.contract_duration}
            ) {
                billing_item_id: id
                item_name
                item_type
                fleet_size
                price_one_time
                price_per_month
                vat
                is_device
                exact_code_id
                kpi_type
              }
          }
        `
        }

        this.setShowLoadingModal(true);
        this.simpleFetch({ data, this_ref: this }).then((res) => {
          if (res) {
            this.priceList = res.priceList;
          }
        }).finally(() => {
          this.setShowLoadingModal(false)
        });
      },
      deleteThisTab() {
        this.deleteTab(this.tab.id);
      },
      fetchCompanies() {
        const data = {
          query: `
          query  {
            CompaniesAll {
              id
              name
            }
          }
        `
        };
        this.setShowLoadingModal(true);
        this.simpleWebAppFetch({ data, this_ref: this })
          .then(res => {
            if (res) {
              this.companies = res.CompaniesAll;
            }
          })
          .finally(() => {
            this.setShowLoadingModal(false);
          });
      },
      changeCompany(value) {
        this.setCompany(value.id)
      }
    },
    computed: {
      ...mapGetters([
        "getUserSystem",
        "getCurrency",
        "getInquiryNew",
        "getUserBrandPermissions",
        "getCompany"
      ]),

      showPayments() {
        if (
          this.tab.data.contract_duration === 0 ||
          this.tab.data.payment_term === 0
        ) {
          return false;
        } else return true;
      },
      isDurationSet() {
        return this.tab.data.contract_duration !== null
          ? this.tab.data.contract_duration >= 0
          : false;
      },
      payments() {
        let temp = this.tab.data.contract_duration / this.tab.data.payment_term;
        if (temp === Infinity) {
          return 1;
        } else {
          return temp;
        }
      },
      oneTime() {
        let tempOneTime = 0;
        this.tab.data.items.forEach(item => {
          if (item.price_one_time > 0) {
            tempOneTime += (item.price_one_time - item.discount) * item.quantity;
          }
        });
        return tempOneTime;
      },
      onePayment() {
        let monthlyTotal = 0;
        this.tab.data.items.forEach(item => {
          if (item.price_per_month > 0) {
            monthlyTotal +=
              (item.price_per_month - item.discount) * item.quantity;
          }
        });

        if (
          this.tab.data.payment_term == 0 &&
          this.tab.data.contract_duration == 0
        ) {
          return monthlyTotal;
        } else {
          return (monthlyTotal * this.tab.data.contract_duration) / this.payments;
        }
      },
      subTotal() {
        return this.oneTime + this.onePayment;
      },
      grandTotal() {
        if (this.payments > 0) {
          return this.oneTime + this.payments * this.onePayment;
        } else {
          return this.oneTime;
        }
      },
      vat() {
        let subtotalvat = 0;
        this.tab.data.items.forEach(item => {
          subtotalvat += this.rowItemTotalVatOnly(
            item.price_one_time,
            item.price_per_month,
            item.quantity,
            this.tab.data.contract_duration,
            item.vat,
            item.discount
          );
        });
        return subtotalvat;
      }
    },
    mounted() {
      this.fetchCompanies();
    }
  };
</script>